import { DateTime, type DurationLikeObject } from "luxon";

import { cache, syncState, type CacheStore } from "@/services/cache";
import { goFetch } from "@/utils/network-utils";
import type { ApiFetchResponse, FetchOptions, PersistedQueryPayload } from "@tmi/types/api/fetch";
// Finding 3/4 (final-review fix wave): `logger.error`/`.warn` are compiled to no-ops in
// production (see console-logger.ts — the whole method-assignment loop is skipped when
// `isProduction`), so a REST containment/degradation signal needs a channel that still does
// something in prod. `clientStateManager` is the established one — `network-utils.ts` already
// posts through it from inside this same sync-worker context, so it's known to function there.
import { endClientErrorStreak, queueClientErrorOncePerStreak } from "@/services/error-services";
import { Unknown } from "@tmi/types/errors";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

function restDegradationStreak( name: string ): string {
    return `rest-degradation:${ name }`;
}

/**
 * Finding 3/4: best-effort production-visible signal for a REST-source degradation that
 * `logger.error`/`.warn` alone would never surface outside dev. Never allowed to throw — a
 * failure reporting a failure must not itself break C1-c containment.
 */
function reportRestDegradation(
    name: string,
    err: any,
    page: number,
    endpoint: string | undefined
): void {
    try {
        // UX roadmap #58: once per failure streak. A failing sync retries every 15 s; the streak ends on a complete sweep.
        queueClientErrorOncePerStreak( restDegradationStreak( name ), err, { endpoint, name, page } );
    } catch {
        /* best-effort — must never break containment */
    }
}

/** What a per-page REST request builder hands back to `fetchFromServer`. */
export interface BuiltPageRequest {
    endpoint: string;
    verbOverride?: string;
    /** Carries e.g. `If-None-Match` on page 0 (see `DependencySyncConfig.buildRequest`). */
    headers?: Record<string, string>;
    payload?: unknown;
}

export interface DependencySyncConfig<TResponse, TApi, TLocal extends { id: string }> {
    name: string;
    store: () => CacheStore< TLocal >;
    syncKey: string;
    throttle: DurationLikeObject;
    /** Required at runtime by the persisted-query (GraphQL) path — the else-branch of
     *  `buildRequest`/`queryId` branching. Optional only in the type so a REST config that
     *  supplies `buildRequest` need not set it. */
    queryId?: string;
    pageSize: number;
    endpoint?: string;
    serverBaseUri?: string;
    /**
     * Per-page REST request builder. When present, `fetchFromServer` branches into a REST GET
     * (or whatever `verbOverride` says) instead of the hardcoded persisted-query POST. `etag` is
     * whatever the caller wants echoed back as `If-None-Match` — typically a previously stored
     * sweep ETag — and is passed through unchanged on every page (the builder itself decides
     * which page, if any, actually carries it as a header; page 0 per the sentient tickers
     * contract).
     */
    buildRequest?: ( page: number, pageSize: number, etag?: string ) => BuiltPageRequest;
    /**
     * Mandatory for REST sources. Evaluated ONCE after loop exit against the LAST page's
     * response, AND-ed with the loop's own `complete` flag — it can only ever downgrade a sweep
     * from complete to partial, never upgrade one (which would re-enable the stale-key prune on
     * a truncated catalog).
     */
    isComplete?: ( response: ApiFetchResponse<TResponse>, fetchedSoFar: number ) => boolean;
    /**
     * Extracts a page's `totalCount` for cross-page pinning (C1-f). Defaults to reading
     * `response.data.totalCount` — the sentient tickers REST contract's shape — so most REST
     * configs need not supply this explicitly.
     */
    getTotalCount?: ( response: ApiFetchResponse<TResponse> ) => number | undefined;
    extractPayload: ( data: ApiFetchResponse<TResponse> ) => TApi[] | undefined | null;
    mapItem: ( item: TApi ) => TLocal;
    postProcess?: ( items: TLocal[] ) => TLocal[];
}

export interface DependencyFetchResult<TLocal> {
    items: TLocal[];
    /**
     * False when a page was lost, i.e. `items` is a subset of what the server holds. A truncated
     * set must never be used to decide that records were deleted upstream — see `syncDependency`.
     */
    complete: boolean;
    /**
     * True when the server answered page 0 with `304 Not Modified` — the sweep was skipped
     * entirely because the caller's ETag was still current. `items` is empty and `complete` is
     * true in this case, but callers MUST branch on `notModified` rather than inferring it from
     * an empty `items` array (an empty array is also what a genuinely empty catalog looks like).
     */
    notModified?: boolean;
    /**
     * The response ETag pinned from page 0 of this sweep. Only meaningful (and only ever
     * returned) when the sweep is complete — a coordinator must never persist an ETag against a
     * partial sweep (C1-g); this field enforces that at the source rather than trusting every
     * caller to remember the rule.
     */
    etag?: string;
}

function buildPageFetchOptions<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    page: number,
    authToken: string | null | undefined,
    priorEtag: string | undefined
): FetchOptions<unknown> {
    if ( config.buildRequest ) {
        const built = config.buildRequest( page, config.pageSize, priorEtag );
        return {
            serverBaseUri: config.serverBaseUri ?? `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
            endpoint: built.endpoint,
            verbOverride: built.verbOverride ?? null,
            headers: built.headers,
            payload: built.payload ?? null,
            authToken: authToken ?? undefined
        };
    }

    // GraphQL persisted-query path — kept byte-for-byte identical to the pre-C1 shape so the
    // sectors/sector-history-style coordinators compile and behave unchanged. `queryId` is
    // optional only in the type; it is required here at runtime.
    if ( !config.queryId ) {
        throw new Error( `[ ${ config.name }-sync-coordinator ] queryId is required when buildRequest is not supplied` );
    }

    return {
        serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
        endpoint: config.endpoint ?? `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
        payload: {
            id: config.queryId,
            variables: {
                skip: page,
                limit: config.pageSize
            }
        } satisfies PersistedQueryPayload,
        authToken: authToken ?? undefined
    };
}

function extractTotalCount<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    response: ApiFetchResponse<TResponse>
): number | undefined {
    if ( config.getTotalCount ) {
        return config.getTotalCount( response );
    }

    const data: any = response.data;
    return typeof data?.totalCount === 'number' ? data.totalCount : undefined;
}

export async function fetchFromServer<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    authToken?: string | null,
    priorEtag?: string
): Promise<DependencyFetchResult<TLocal>> {
    const t0 = performance.now();
    let allItems: TLocal[] = [];
    let page = 0;
    let complete = true;
    let pagePayload: TLocal[] = [];
    let fetchedSoFar = 0;
    let pinnedEtag: string | undefined;
    let pinnedTotalCount: number | undefined;
    let lastResponse: ApiFetchResponse<TResponse> | undefined;

    do {
        let response: ApiFetchResponse<TResponse> | null;
        let requestOptions: FetchOptions<unknown> | undefined;
        try {
            // Finding 3: `priorEtag` (-> `If-None-Match`) is only ever meaningful on page 0 — a
            // builder receiving it on a later page could accidentally send it mid-sweep.
            requestOptions = buildPageFetchOptions( config, page, authToken, 0 === page ? priorEtag : undefined );
            response = await goFetch<TResponse, unknown>( requestOptions );
        } catch ( err: any ) {
            if ( !config.buildRequest ) {
                // Finding 1 (Critical): the five GraphQL persisted-query coordinators
                // (category/industry-sector/tag/product/simple-page) depend on a typed HTTP
                // error propagating out of `fetchFromServer` unchanged — their
                // `refresh*FromServer()` wrappers and outer `.catch` handlers are the actual
                // error containment for that path, and silently swallowing it here turned a
                // transient 500 into "the catalog is now empty" with no catch ever firing.
                // C1-c containment is REST-only: only a config with `buildRequest` gets it.
                throw err;
            }

            // C1-c: every typed HTTP error (BadRequestError-parsed RuntimeException, Unauthorized,
            // InternalError, ApiError) is a RuntimeException subclass and therefore throws out of
            // goFetch. Left uncaught for a REST source, this would propagate all the way to
            // syncDependency's outer catch, skipping the partial marking, the no-prune decision
            // AND the retry threshold write. Contain it here instead: mark the sweep partial and
            // stop paging.
            complete = false;
            logger.error( `[ ${ config.name }-sync-coordinator ] page ${ page } request threw — treating the result set as partial`, err );
            // Finding 3: logger.error alone is a production no-op — surface through the channel
            // that still does something there.
            reportRestDegradation( config.name, err, page, requestOptions?.endpoint );
            break;
        }

        if ( !response ) {
            // A failed request is indistinguishable from "the server has nothing more" once the
            // loop exits, so record it explicitly rather than letting the caller assume the set
            // is authoritative and prune against it.
            complete = false;
            logger.warn( `[ ${ config.name }-sync-coordinator ] page ${ page } request failed — treating the result set as partial` );
            break;
        }

        if ( 304 === response.status ) {
            if ( 0 === page ) {
                // C1-b: the server confirmed nothing changed since `priorEtag`, sent as
                // `If-None-Match` on page 0 only. This short-circuits the whole sweep — there is
                // nothing more to page through.
                endClientErrorStreak( restDegradationStreak( config.name ) );
                return { items: [], complete: true, notModified: true };
            }

            // Finding 3: a 304 on a LATER page is not a valid revalidation outcome — page 0
            // already returned real data, so honoring it here would silently discard everything
            // already collected while still committing the full throttle. Treat it like any
            // other unexpected page response: partial, stop paging.
            complete = false;
            logger.error( `[ ${ config.name }-sync-coordinator ] unexpected 304 on page ${ page } — treating the result set as partial` );
            break;
        }

        // Finding 2 (Important): C1-f sweep pinning is REST-only. Page 0's ETag/totalCount
        // becoming the sweep's baseline is only meaningful for a source that actually sends
        // If-None-Match and paginates against a stable totalCount contract — the sentient tickers
        // REST endpoint. A GraphQL config never sends If-None-Match and gets no benefit from
        // this; if the content gateway/CDN ever attached an exposed ETag to a GraphQL POST
        // response, ungated pinning would let cross-page drift permanently stall a multi-page
        // GraphQL sweep (never complete, never prune, 15-second threshold forever) for zero
        // upside. Gate the entire block on `buildRequest`, the same predicate C1-c and
        // `persistEtag` already use.
        if ( config.buildRequest ) {
            const pageTotalCount = extractTotalCount( config, response );
            if ( 0 === page ) {
                pinnedEtag = response.etag;
                pinnedTotalCount = pageTotalCount;

                // Finding 4: the S7/CORS failure mode — `Access-Control-Expose-Headers: ETag`
                // missing from the gateway response — makes `response.etag` undefined on every
                // request. The client then looks perfectly healthy while silently never sending
                // If-None-Match and never pinning: `persistEtag(undefined)` just deletes the key
                // every complete sweep instead of erroring. Surface this loudly rather than let it
                // degrade silently.
                if ( undefined === response.etag ) {
                    logger.warn( `[ ${ config.name }-sync-coordinator ] page 0 response carried no ETag — If-None-Match/pinning will be inert for this sweep (check Access-Control-Expose-Headers on the gateway)` );
                    reportRestDegradation(
                        config.name,
                        new Unknown( `[ ${ config.name }-sync-coordinator ] missing page-0 ETag — If-None-Match/pinning inert (check Access-Control-Expose-Headers)` ),
                        page,
                        requestOptions?.endpoint
                    );
                }
            } else {
                if ( undefined !== pinnedEtag && undefined !== response.etag && response.etag !== pinnedEtag ) {
                    complete = false;
                    logger.error( `[ ${ config.name }-sync-coordinator ] response ETag changed mid-sweep on page ${ page } — treating the result set as partial` );
                    reportRestDegradation(
                        config.name,
                        new Unknown( `[ ${ config.name }-sync-coordinator ] response ETag changed mid-sweep on page ${ page }` ),
                        page,
                        requestOptions?.endpoint
                    );
                }
                if ( undefined !== pinnedTotalCount && undefined !== pageTotalCount && pageTotalCount !== pinnedTotalCount ) {
                    complete = false;
                    logger.error( `[ ${ config.name }-sync-coordinator ] totalCount changed mid-sweep on page ${ page } (${ pinnedTotalCount } -> ${ pageTotalCount }) — treating the result set as partial` );
                    reportRestDegradation(
                        config.name,
                        new Unknown( `[ ${ config.name }-sync-coordinator ] totalCount changed mid-sweep on page ${ page } (${ pinnedTotalCount } -> ${ pageTotalCount })` ),
                        page,
                        requestOptions?.endpoint
                    );
                }
            }
        }

        lastResponse = response;

        // Finding 6: `extractPayload`/`mapItem` are caller-supplied and can throw (a malformed
        // response shape, a mapper that trips over unexpected data — e.g. the ticker
        // coordinator's own R17 validation could plausibly be reshaped into a throw by a future
        // edit). Pre-C1, only the `goFetch` call itself was contained; a throw here escaped
        // straight past the do-while loop into `syncDependency`'s outer catch, which writes no
        // retry threshold — the exact "silently stuck with no retry" failure shape C1-c exists to
        // remove. Contain it the same way, REST-only (GraphQL keeps its pre-C1 rethrow contract).
        try {
            const raw = config.extractPayload( response );
            if ( raw ) {
                pagePayload = raw.map( config.mapItem );
                fetchedSoFar += pagePayload.length;

                const t1 = performance.now();
                logger.info( `Fetching & mapping ${ pagePayload.length } ${ config.name } items took ${ t1 - t0 } ms` );

                allItems = [ ...pagePayload, ...allItems ];
            } else {
                complete = false;
                logger.warn( `[ ${ config.name }-sync-coordinator ] page ${ page } carried no payload — treating the result set as partial` );
                break;
            }
        } catch ( err: any ) {
            if ( !config.buildRequest ) {
                throw err;
            }

            complete = false;
            logger.error( `[ ${ config.name }-sync-coordinator ] page ${ page } extraction/mapping threw — treating the result set as partial`, err );
            reportRestDegradation( config.name, err, page, requestOptions?.endpoint );
            break;
        }

        page++;
    } while ( pagePayload.length >= config.pageSize );

    // C1-f: `isComplete` is evaluated ONCE, after loop exit, against the last page's response,
    // and AND-ed with the loop's own verdict — it can only downgrade `complete`, never upgrade a
    // sweep the loop itself already marked partial.
    if ( config.isComplete && lastResponse ) {
        try {
            complete = complete && config.isComplete( lastResponse, fetchedSoFar );
        } catch ( err: any ) {
            // Finding 6: same containment contract as the extraction/mapping step above — a
            // throwing `isComplete` for a REST config must not escape uncontained either.
            if ( !config.buildRequest ) {
                throw err;
            }

            complete = false;
            logger.error( `[ ${ config.name }-sync-coordinator ] isComplete threw — treating the result set as partial`, err );
            reportRestDegradation( config.name, err, page, undefined );
        }
    }

    if ( config.postProcess ) {
        allItems = config.postProcess( allItems );
    }

    if ( complete ) {
        endClientErrorStreak( restDegradationStreak( config.name ) );
    }

    // C1-g: the generic layer never persists an ETag, but it must not even OFFER one for a
    // partial sweep — the coordinator that eventually writes it (a later task) can only obey
    // "write on complete, delete on partial" if a partial sweep hands back `undefined`.
    return { items: allItems, complete, etag: complete ? pinnedEtag : undefined };
}

/**
 * Hydrates `map` from IDB — swap-only-on-full-success, per Finding 2 (never clear a long-lived
 * worker's map up front and leave it half-populated if the IDB read throws partway through).
 * Extracted out of `syncDependency`'s `finally` block so a coordinator can also call it directly
 * for an IDB-only refresh without attempting a network fetch — e.g. the ticker coordinator's
 * no-token guard (C2/R8), which must still surface whatever is already cached.
 */
export async function hydrateMapFromStore<TLocal extends { id: string }>(
    store: () => CacheStore< TLocal >,
    map: Map<string, TLocal>,
    name: string
): Promise<void> {
    try {
        const hydrated = new Map<string, TLocal>();
        for ( const item of await store().all() ) {
            hydrated.set( item.id, item );
        }
        map.clear();
        hydrated.forEach( ( value, key ) => map.set( key, value ) );
    } catch ( hydrateErr: any ) {
        logger.error( `[ ${ name }-sync-coordinator ] map hydration from IDB failed:`, hydrateErr );
    }
}

export async function syncDependency<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    map?: Map<string, TLocal>,
    authToken?: string | null,
    priorEtag?: string
): Promise<boolean> {
    let updated = false;
    // C1-d: the map must be hydrated from IDB on EVERY path — a thrown error, a 304, a partial
    // sweep, everything — because this runs inside a long-lived worker and an unhydrated map
    // silently sets `tickers: []` (or the equivalent) for every consumer for the rest of the
    // session. Only skip the (redundant) IDB re-read when the map has already been brought fully
    // current another way.
    let mapAlreadyCurrent = false;

    // C1b/C1-g: the sweep's ETag lives in a sibling syncState key derived from the (possibly
    // rotated, C4) syncKey. Gated on `buildRequest` (REST-only) so a GraphQL config never reads
    // or writes this key at all — the rule stays source-agnostic without touching GraphQL
    // behavior in any way, not even an extra no-op syncState call.
    const etagKey = `${ config.syncKey }:etag`;
    const persistEtag = async ( etag: string | undefined ): Promise<void> => {
        if ( !config.buildRequest ) { return; }
        if ( etag ) {
            await syncState.put( etagKey, etag );
        } else {
            await syncState.delete( etagKey );
        }
    };

    try {
        const t0 = performance.now();

        await cache.ready();

        const store = config.store();

        const syncThresholdInMillis = await syncState.get< number >( config.syncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            // C1b: thread a caller-supplied `priorEtag` through unchanged when given (tests, or a
            // future caller that already knows it); otherwise read the persisted sweep ETag for
            // REST sources. A GraphQL config never has one written, so this is always `undefined`
            // for them — behavior-neutral.
            const effectivePriorEtag = ( config.buildRequest && undefined === priorEtag )
                ? ( ( await syncState.get< string >( etagKey ) ) ?? undefined )
                : priorEtag;

            const { items: received, complete, notModified, etag } = await fetchFromServer( config, authToken, effectivePriorEtag );

            if ( notModified ) {
                // Finding 5: a 304 against an EMPTY local store is a stored-ETag/IDB desync
                // signal (e.g. the store was cleared by a schema migration but the ETag survived
                // in a different key) — committing the full throttle here would blackout an
                // empty catalog for the entire throttle window. Fall through to the short retry
                // instead so the next attempt (which will not have a matching ETag once this one
                // is inspected) has a chance to repopulate the store soon.
                if ( await store.isEmpty() ) {
                    // C1-g extension: without clearing the stored ETag here, the 15-second retry
                    // below would echo the SAME (suspect) `If-None-Match` and get the SAME 304
                    // forever, permanently stuck against an empty store. Delete it so the retry is
                    // a clean, unconditional fetch.
                    await persistEtag( undefined );
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.put( config.syncKey, nextSyncThresholdInMillis );
                    logger.error( `[ ${ config.name }-sync-coordinator ] 304 Not Modified against an EMPTY local store — desync suspected, forcing a short retry instead of the full throttle` );
                } else {
                    // C1-b: full throttle, no prune, no 15-second write, IDB left exactly as-is —
                    // the server confirmed the store we already hold is still current.
                    const nextSyncThresholdInMillis = DateTime.utc().plus( config.throttle ).toMillis();
                    await syncState.put( config.syncKey, nextSyncThresholdInMillis );
                    logger.info( `[ ${ config.name }-sync-coordinator ] 304 Not Modified — keeping the existing store, full throttle committed` );
                }
            } else if ( complete && ( received.length > 0 || !!config.buildRequest ) ) {
                // Finding 4 (T5): gate the success path on `complete`, not `received.length > 0`,
                // for a REST source. A complete-but-empty sweep from the sentient tickers endpoint
                // is an authentic state (the server's publish guard refuses to serve an empty
                // catalog, so a complete empty response means the catalog really is now empty) and
                // must prune everything, not sit in the 15-second retry branch forever because
                // there was nothing to write.
                //
                // Finding 1 (final-review fix wave, Critical): that "empty-complete is authentic"
                // reasoning is a property of the sentient REST endpoint's publish guard ONLY. The
                // six GraphQL persisted-query coordinators (author/category/industry-sector/
                // product/tag/simple-page) reach this same branch, and for them a 200 carrying `[]`
                // is NOT necessarily authentic — a bad content deploy, an entitlement blip, or a
                // non-critical GraphQL error that still resolves `result.data` all look identical
                // to "the catalog is empty" here. Pre-Finding-4 behavior for that case was no
                // prune + a 15-second retry threshold; requiring `received.length > 0` OR
                // `buildRequest` (REST) restores exactly that behavior for GraphQL by routing a
                // GraphQL complete-empty sweep into the `else` branch below instead of pruning the
                // entire store on the strength of a response this layer cannot trust for that
                // source.
                updated = true;

                if ( received.length > 0 ) {
                    // WRITE FIRST. This used to delete before writing, so any failure in between
                    // left the store emptier than it started and the user staring at missing data.
                    await store.putMany( received );
                }

                // Complete sweep: prune against the FULL received set — which, if empty, prunes
                // everything. That is correct here precisely because `complete` says the server
                // was not truncated.
                const receivedIds = new Set( received.map( item => item.id ) );
                const keysToRemove = ( await store.keys() ).filter( key => !receivedIds.has( key ) );
                await store.deleteMany( keysToRemove );

                // C1-g: write the sweep's ETag ONLY on a complete sweep — `fetchFromServer`
                // already enforces this by only ever returning `etag` when `complete` is true, so
                // this call is safe unconditionally here.
                await persistEtag( etag );

                const nextSyncThresholdInMillis = DateTime.utc().plus( config.throttle ).toMillis();
                await syncState.put( config.syncKey, nextSyncThresholdInMillis );

                const t1 = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ received.length } ${ config.name } items from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );

                // Populate map directly from server data — skip redundant IDB re-read.
                if ( map ) {
                    map.clear();
                    received.forEach( item => map.set( item.id, item ) );
                    mapAlreadyCurrent = true;
                }
            } else {
                // Partial sweep — includes a typed HTTP error now that C1-c contains it inside
                // fetchFromServer for REST sources instead of letting it throw out here. Write
                // whatever was collected (best effort), never prune, retry soon rather than
                // holding a permanent cooldown against a transient failure.
                if ( received.length > 0 ) {
                    updated = true;
                    await store.putMany( received );
                    logger.warn( `[ ${ config.name }-sync-coordinator ] partial fetch — skipping the stale-record purge` );
                } else {
                    logger.error( `[ ${ config.name }-sync-coordinator ] sync produced no data (see prior warnings) — marking partial and retrying shortly` );
                }

                // C1-g: never leave an ETag standing for a sweep that didn't complete — a later
                // complete sweep must not skip pages a partial sweep never actually saw.
                await persistEtag( undefined );

                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.put( config.syncKey, nextSyncThresholdInMillis );
            }
        } else if ( map && map.size > 0 ) {
            mapAlreadyCurrent = true;
        }
    } catch ( err: any ) {
        logger.error( `[ ${ config.name }-sync-coordinator ] Sync failed:`, err );
        // Defensive: an unexpected throw here (e.g. an IDB access failure) means we can't vouch
        // for the sweep at all — never leave a possibly-stale ETag standing on the strength of it.
        try {
            await persistEtag( undefined );
        } catch { /* best-effort — already logging the primary failure above */ }

        // Finding 6 backstop: whatever escaped all the way here — fetchFromServer's own
        // containment declining for a GraphQL config (by design), or a throw from entirely
        // outside fetchFromServer (an IDB failure, for instance) — must not sit on whatever
        // threshold was last committed with no retry ever scheduled. Write the same 15-second
        // partial-retry threshold the contained path already uses, for every source.
        try {
            await syncState.put( config.syncKey, DateTime.utc().plus( { seconds: 15 } ).toMillis() );
        } catch { /* best-effort — already logging the primary failure above */ }
    } finally {
        // Runs on every path — success, notModified, partial, throttle-skip, AND a thrown error —
        // which is the whole point of C1-d.
        if ( map && !mapAlreadyCurrent ) {
            await hydrateMapFromStore( config.store, map, config.name );
        }
    }

    return updated;
}
